/**
 * @fileoverview Template API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Template module.
 * Handles AI prompt templates for learning type analysis.
 */

import { z } from 'zod';
import {
	createContract,
	ApiSuccessResponseSchema,
} from './contract.utils';
import { TemplateType, TemplateVariablesSchema } from './analysis.contract';

// Re-export for convenience
export { TemplateType };
export type { TemplateVariables } from './analysis.contract';

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Template Metadata Schema
 */
export const TemplateMetadataSchema = z.object({
	name: z.string(),
	description: z.string(),
	requiredVariables: z.array(z.string()),
	outputLanguage: z.string(),
	targetAudience: z.string(),
});
export type TemplateMetadata = z.infer<typeof TemplateMetadataSchema>;

/**
 * Template Validation Result Schema
 */
export const TemplateValidationResultSchema = z.object({
	isValid: z.boolean(),
	missingVariables: z.array(z.string()),
	extraVariables: z.array(z.string()),
});
export type TemplateValidationResult = z.infer<typeof TemplateValidationResultSchema>;

/**
 * Format Template Request Schema
 */
export const FormatTemplateRequestSchema = z.object({
	templateType: z.enum(TemplateType),
	variables: TemplateVariablesSchema,
});
export type FormatTemplateRequest = z.infer<typeof FormatTemplateRequestSchema>;

/**
 * Format Template Response Schema
 */
export const FormatTemplateResponseSchema = z.object({
	prompt: z.string(),
	templateType: z.enum(TemplateType),
	variablesUsed: z.array(z.string()),
});
export type FormatTemplateResponse = z.infer<typeof FormatTemplateResponseSchema>;

/**
 * Validate Template Request Schema
 */
export const ValidateTemplateRequestSchema = z.object({
	templateType: z.enum(TemplateType),
	variables: TemplateVariablesSchema,
});
export type ValidateTemplateRequest = z.infer<typeof ValidateTemplateRequestSchema>;

/**
 * Get Template Metadata Response Schema
 */
export const GetTemplateMetadataResponseSchema = z.object({
	templates: z.record(z.enum(TemplateType), TemplateMetadataSchema),
});
export type GetTemplateMetadataResponse = z.infer<typeof GetTemplateMetadataResponseSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

/**
 * Format Template API Response
 * { success: true, data: FormatTemplateResponse, timestamp: string }
 */
export const FormatTemplateApiResponseSchema = ApiSuccessResponseSchema(
	FormatTemplateResponseSchema,
);

/**
 * Template Validation Result API Response
 * { success: true, data: TemplateValidationResult, timestamp: string }
 */
export const TemplateValidationResultApiResponseSchema = ApiSuccessResponseSchema(
	TemplateValidationResultSchema,
);

/**
 * Template Metadata API Response
 * { success: true, data: TemplateMetadata, timestamp: string }
 */
export const TemplateMetadataApiResponseSchema = ApiSuccessResponseSchema(TemplateMetadataSchema);

/**
 * Get Template Metadata API Response
 * { success: true, data: GetTemplateMetadataResponse, timestamp: string }
 */
export const GetTemplateMetadataApiResponseSchema = ApiSuccessResponseSchema(
	GetTemplateMetadataResponseSchema,
);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Template API Contract
 *
 * Defines the complete API surface for the Template module.
 * Both frontend and backend use this as the single source of truth.
 */
export const TEMPLATE_API_CONTRACT = createContract('/template', {
	/**
	 * POST /template/format
	 * Format a template with variables
	 */
	format: {
		method: 'POST' as const,
		path: '/format',
		body: FormatTemplateRequestSchema,
		response: FormatTemplateApiResponseSchema,
	},

	/**
	 * POST /template/validate
	 * Validate template variables
	 */
	validate: {
		method: 'POST' as const,
		path: '/validate',
		body: ValidateTemplateRequestSchema,
		response: TemplateValidationResultApiResponseSchema,
	},

	/**
	 * GET /template/metadata
	 * Get metadata for all templates
	 */
	getMetadata: {
		method: 'GET' as const,
		path: '/metadata',
		response: GetTemplateMetadataApiResponseSchema,
	},

	/**
	 * GET /template/:templateType/metadata
	 * Get metadata for a specific template
	 */
	getTemplateMetadata: {
		method: 'GET' as const,
		path: '/:templateType/metadata',
		params: z.object({
			templateType: z.enum(TemplateType),
		}),
		response: TemplateMetadataApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post decorators
 */
export const TEMPLATE_ROUTES = {
	base: TEMPLATE_API_CONTRACT.base,
	format: TEMPLATE_API_CONTRACT.routes.format.path,
	validate: TEMPLATE_API_CONTRACT.routes.validate.path,
	getMetadata: TEMPLATE_API_CONTRACT.routes.getMetadata.path,
	getTemplateMetadata: TEMPLATE_API_CONTRACT.routes.getTemplateMetadata.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type TemplateApiContract = typeof TEMPLATE_API_CONTRACT;
